import { useCallback } from 'react';

import {
  getCreatedResourceMessage,
  RESOURCE_CONTENT_TYPE,
  RESOURCE_TAG,
} from 'src/data/performance';
import { getCustomerPlan } from 'src/data/subscription';
import * as events from 'src/lib/events';
import { useFreshRef } from 'src/lib/hooks';
import { captureMessage } from 'src/lib/sentry';
import { useErrorHandler } from 'src/services/useErrorHandler';
import { PAGES, useTrackingEvent } from 'src/services/useTrackingEvent';
import { useCustomerActions } from 'src/store/customer';
import { useNotificationActions } from 'src/store/notification';
import { useResourceActions } from 'src/store/resource';
import { useResourcePopup } from 'src/views/ResourcePopup';

export const useAddResource = (trackingEventAttributes = {}) => {
  const openAddResourcePopup = useResourcePopup();
  const resourceActions = useResourceActions();
  const customerActions = useCustomerActions();
  const notificationActions = useNotificationActions();
  const trackingEvent = useTrackingEvent();
  const handleError = useErrorHandler();

  const trackingEventAttributesRef = useFreshRef(trackingEventAttributes);

  return useCallback(
    async (urls = [], tags) => {
      let createdResources = [];
      if (urls.length > 0) {
        try {
          const existenceResult = await resourceActions.checkUrlResourcesExistence(urls);
          const nonExistingUrls = existenceResult.reduce(
            (acc, res) => (res.exist ? acc : [...acc, res.content]),
            []
          );
          if (nonExistingUrls.length > 0) {
            const list = nonExistingUrls.map((url) => ({
              contentType: RESOURCE_CONTENT_TYPE.url,
              content: url,
              tags: tags || [RESOURCE_TAG.general],
            }));
            const createdResult = await resourceActions.createUrlResourcesSync(list);
            if (createdResult?.length > 0) {
              createdResources = createdResult;
            }
          }
        } catch (error) {
          captureMessage('Error when adding a resource', {
            level: 'warning',
            extra: {
              error: error.toString(),
              source: 'src/services/useAddResource.js -> useAddResource',
              urls,
              currentCustomerId: customerActions.getCurrentCustomerId(),
            },
          });

          // add *sync resource* is a side effect action, when using URLs in tov, personas, blog, etc.
          // we want to hide this error from the user and continue with the original action
          return true;
        }
      } else {
        const res = await openAddResourcePopup();
        if (res) {
          createdResources = [res];
        }
      }

      const createdResourcesLength = createdResources.length;
      if (createdResourcesLength > 0) {
        const customer = customerActions.getCurrentCustomer();
        trackingEvent(events.RESOURCES.resourceCreated, {
          page: PAGES.resourcesIntegrations,
          ...trackingEventAttributesRef.current,
          plan: getCustomerPlan(customer),
          resourceType: RESOURCE_CONTENT_TYPE.url,
          autoCreation: !!urls.length,
        });

        const message =
          createdResourcesLength > 1
            ? `${createdResourcesLength} resources have been successfully added to Anyword`
            : getCreatedResourceMessage(createdResources[0]);

        resourceActions.queryWorkspaceResources().catch(handleError);
        customerActions.refreshCurrentCustomerFeatureLimit();
        notificationActions.displaySuccess(message, null, { timeout: 5000 });
      }
      return createdResources;
    },
    [
      resourceActions,
      openAddResourcePopup,
      customerActions,
      trackingEvent,
      trackingEventAttributesRef,
      handleError,
      notificationActions,
    ]
  );
};
